import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Define the Form990Data interface
export interface Form990Data {
  header?: {
    fiscalYear?: string;
    ein?: string;
    organizationName?: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      zip?: string;
    };
    formattedAddress?: {
      street?: string;
      city?: string;
      state?: string;
      zip?: string;
      cityStateZip?: string;
    };
  };
  revenue?: {
    contributions?: number;
    programService?: number;
    investment?: number;
    other?: number;
    total?: number;
  };
  expenses?: {
    programServices?: number;
    management?: number;
    fundraising?: number;
    total?: number;
  };
  netAssets?: {
    beginning?: number;
    end?: number;
  };
}

// Create a local function to get auth data from localStorage
const getAuthData = () => {
  try {
    const userStr = localStorage.getItem('user');
    const orgStr = localStorage.getItem('organization');
    
    const user = userStr ? JSON.parse(userStr) : null;
    const organization = orgStr ? JSON.parse(orgStr) : null;
    
    return { user, organization };
  } catch (error) {
    console.error('Error parsing auth data from localStorage:', error);
    return { user: null, organization: null };
  }
};

export const useForm990Data = (fiscalSponsorId?: string, year?: string) => {
  // Use the local function instead of the hook
  const { user, organization } = getAuthData();
  
  // Try to get fiscalSponsorId from various possible locations
  const userFiscalSponsorId = 
    fiscalSponsorId || 
    (user?.fiscalSponsorId) || 
    (user?.organization?._id) || 
    (organization?._id) || 
    '';
  
  const [state, setState] = useState<{
    data: Form990Data | null;
    form990Data: Form990Data | null; // Added for compatibility
    loading: boolean;
    isLoading: boolean; // Added for compatibility
    error: string | null;
  }>({
    data: null,
    form990Data: null,
    loading: false,
    isLoading: false,
    error: null
  });

  const transformAddressData = (data: Form990Data): Form990Data => {
    if (!data?.header?.address) return data;

    const { city, state, zip } = data.header.address;
    
    // Format city, state zip as a single line
    const formattedCityStateZip = [
      city,
      state && zip ? `${state} ${zip}` : state || zip
    ].filter(Boolean).join(', ');
    
    return {
      ...data,
      header: {
        ...data.header,
        formattedAddress: {
          ...data.header.address,
          cityStateZip: formattedCityStateZip
        }
      }
    };
  };

  const fetchForm990Data = useCallback(async () => {
    // Use provided fiscalSponsorId or get from user
    const fsId = userFiscalSponsorId;
    
    if (!fsId) {
      setState(prev => ({
        ...prev,
        error: 'No fiscal sponsor ID available'
      }));
      return;
    }
    
    setState(prev => ({ 
      ...prev, 
      loading: true,
      isLoading: true,
      error: null 
    }));
    
    try {
      const yearParam = year ? `?year=${year}` : '';
      const response = await axios.get(`/api/fiscal-sponsors/${fsId}/form990${yearParam}`);
      
      const transformedData = transformAddressData(response.data);
      
      setState(prev => ({ 
        ...prev, 
        data: transformedData,
        form990Data: transformedData,
        loading: false,
        isLoading: false
      }));
    } catch (error: any) {
      setState(prev => ({ 
        ...prev, 
        loading: false,
        isLoading: false,
        error: error.message || 'Failed to fetch Form 990 data'
      }));
    }
  }, [userFiscalSponsorId, year]);

  const regenerate990 = useCallback(async () => {
    const fsId = userFiscalSponsorId;
    
    if (!fsId) {
      setState(prev => ({
        ...prev,
        error: 'No fiscal sponsor ID available'
      }));
      return null;
    }
    
    setState(prev => ({ 
      ...prev, 
      loading: true,
      isLoading: true,
      error: null 
    }));
    
    try {
      const response = await axios.post(`/api/fiscal-sponsors/${fsId}/form990/regenerate`);
      
      const transformedData = transformAddressData(response.data);
      
      setState(prev => ({ 
        ...prev, 
        data: transformedData,
        form990Data: transformedData,
        loading: false,
        isLoading: false
      }));
      
      return transformedData;
    } catch (error: any) {
      setState(prev => ({ 
        ...prev, 
        loading: false,
        isLoading: false,
        error: error.message || 'An error occurred while regenerating Form 990'
      }));
      return null;
    }
  }, [userFiscalSponsorId]);

  useEffect(() => {
    fetchForm990Data();
  }, [fetchForm990Data]);

  return {
    ...state,
    regenerate990
  };
}; 